/**
 * SMSWiser campaign detail and stats queries (drops, lists, texts, gateways)
 * For Media Devoted | Created By Ciro Autiero
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type {
  DropsWithStatsResponse,
  ListsWithStatsResponse,
  GatewaysWithStatsResponse,
  TextsWithStatsResponse,
  SmswiserCampaignDetail,
} from '@/features/smswiser-campaigns/types/workspace.types';
import api from '@/services/api';

import { keys } from '../keys';
import { useWithStatsTracker } from '../stats-tracker';
import { unwrapApiData } from '../utils';

/**
 * Year-long Binom list reports can outlast the shared 45s client timeout.
 * The server allows 60s per attempt and retries twice (~3 min). Stay above that.
 */
export const LISTS_STATS_TIMEOUT_MS = 240_000;

export interface DropsWithStatsInput {
  countryKey: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  start?: number;
  length?: number;
  tz?: string;
  currency?: string;
  enabled?: boolean;
}

export interface ListsWithStatsInput {
  countryKey: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  tz?: string;
  currency?: string;
  enabled?: boolean;
}

export interface TextsWithStatsInput {
  countryKey: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  tz?: string;
  currency?: string;
  limit?: number;
  groupBy?: 'drop' | 'gateway';
  enabled?: boolean;
}

export type GatewaysWithStatsInput = ListsWithStatsInput;

export function useSmswiserCampaignDetail(countryKey: string, wiserId: number) {
  return useQuery({
    queryKey: keys.detail(countryKey, wiserId),
    queryFn: async () => {
      const res = await api.get(`/smswiser/campaigns/${countryKey}/${wiserId}`);
      return unwrapApiData<SmswiserCampaignDetail>(res.data);
    },
    enabled: Boolean(countryKey) && Number.isFinite(wiserId) && wiserId > 0,
    staleTime: 30_000,
  });
}

export function useSmswiserCampaignDropsWithStats(input: DropsWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string | number> = useWithStatsTracker({
    start: input.start ?? 0,
    length: input.length ?? 100,
  });
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;
  const isGrouped = wiserIds.length > 1;
  if (isGrouped) params.wiserIds = wiserIds.join(',');
  const url = isGrouped
    ? `/smswiser/campaigns/${input.countryKey}/drops-with-stats`
    : `/smswiser/campaigns/${input.countryKey}/${wiserIds[0]}/drops-with-stats`;

  return useQuery({
    queryKey: keys.dropsWithStats(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(url, { params });
      return unwrapApiData<DropsWithStatsResponse>(res.data);
    },
    enabled: (input.enabled ?? true) && wiserIds.length > 0 && wiserIds[0] > 0,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useListsWithStats(input: ListsWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = useWithStatsTracker({ wiserIds: wiserIds.join(',') });
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;

  return useQuery({
    queryKey: keys.listsWithStats(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/lists-with-stats`,
        { params, timeout: LISTS_STATS_TIMEOUT_MS },
      );
      return unwrapApiData<ListsWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useTextsWithStats(input: TextsWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = useWithStatsTracker({ wiserIds: wiserIds.join(',') });
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;
  if (typeof input.limit === 'number' && input.limit >= 100 && input.limit <= 5000) {
    params.limit = String(input.limit);
  }
  if (input.groupBy === 'gateway') params.groupBy = 'gateway';

  return useQuery({
    queryKey: keys.textsWithStats(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/texts-with-stats`,
        { params },
      );
      return unwrapApiData<TextsWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useGatewaysWithStats(input: GatewaysWithStatsInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = useWithStatsTracker({ wiserIds: wiserIds.join(',') });
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;

  return useQuery({
    queryKey: keys.gatewaysWithStats(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/gateways-with-stats`,
        { params },
      );
      return unwrapApiData<GatewaysWithStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
